import { useEffect, useState } from 'react';

/**
 * The three form primitives the Users, Groups & Permissions page is built from.
 *
 * MOVED HERE VERBATIM in V0.46, when the API-keys tab arrived and needed the same three. They
 * were local to `PermissionsPage.tsx`, and the alternative to lifting them was a second copy of
 * a checkbox row — which is exactly the failure this codebase has already paid for once. Their
 * markup and classes are unchanged (`.chk`, `.chk-sub`, `.perm-fieldset`, `.fs-lbl`, `.fld`),
 * because `e2e/30-permissions.spec.ts` counts them.
 *
 * `PasswordField` JOINED THEM in V0.48, when the Backups tab's passphrase became its second and
 * third caller. `Highlight` still has exactly one and stays where it is: lifting a thing with
 * one user is how a shared module fills up with private code.
 */

export function Check({
  id,
  label,
  sub,
  checked,
  disabled,
  onChange,
}: {
  /** Optional, and lands on the INPUT: a spec that has to tick one box needs a handle. */
  id?: string;
  label: string;
  sub?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="chk">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {sub ? <span className="chk-sub">{sub}</span> : null}
      </span>
    </label>
  );
}

export function Fieldset({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="perm-fieldset">
      <span className="fs-lbl">{title}</span>
      {children}
    </div>
  );
}

/** Saves on blur, and puts back what the server still holds if the save is refused. */
export function AutoField({
  value,
  placeholder,
  title,
  readOnly,
  onSave,
}: {
  value: string;
  placeholder?: string;
  title?: string;
  readOnly?: boolean;
  onSave?: (raw: string) => Promise<string | null>;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <input
      className="fld"
      style={{ minWidth: 120 }}
      autoComplete="off"
      placeholder={placeholder}
      title={title}
      readOnly={readOnly}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={async () => {
        if (readOnly || !onSave || text === value) return;
        const settled = await onSave(text);
        if (settled !== null) setText(settled);
      }}
    />
  );
}

/**
 * A password field with a reveal toggle.
 *
 * MOVED HERE in V0.48, when the Backups tab needed one for a bundle's passphrase. The note
 * above says lifting a thing with one user is how a shared module fills up with private
 * code — it had one user then and has three now (a user's password, an export passphrase, a
 * restore passphrase), which is the condition that note was waiting on.
 *
 * Markup and classes unchanged (`.pwd-wrap`, `.fld`, `.pwd-toggle`), because the admin page's
 * spec drives the existing caller through them.
 */
export function PasswordField({
  placeholder,
  value,
  onChange,
  onCommit,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  onCommit?: () => void;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="pwd-wrap">
      {/* `new-password`, never left to the browser (V0.54.5). Every password box on the
          permissions page SETS something — a user's password, a backup passphrase — and none is
          a sign-in. Left unmarked, Chrome's password manager took each one for a login form and
          filled the saved username into the nearest text box above it: the page's search box,
          whose change handler switches to Users & Groups, so the first click on Backups (whose
          passphrase box appears with the tab) bounced straight back with "admin" in the search.
          It could equally have filled a user's username field, which saves on blur. */}
      <input
        className="fld"
        style={{ minWidth: 120 }}
        type={shown ? 'text' : 'password'}
        autoComplete="new-password"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
      />
      <button type="button" className="pwd-toggle" onClick={() => setShown((s) => !s)}>
        <svg className="ico" style={{ width: 16, height: 16 }}>
          <use href={shown ? '#i-eye-off' : '#i-eye'} />
        </svg>
      </button>
    </div>
  );
}
